/** @format */
import React, { useEffect, useState, lazy, Suspense } from 'react';

import type { ITheme } from 'interfaces';

import { default as packageInfo } from '../package.json';

const GlobalStyles = lazy(async () => {
  const module = await import('styles');
  return { default: module.GlobalStyles };
});

const ReportCenter = lazy(async () => await import(`./components/ReportCenter`));

function App({ appCode }: { appCode: string }): React.ReactElement {
  const [isAppShown, setIsAppShown] = useState<boolean>(false);
  const [theme, setTheme] = useState<ITheme>('light');

  // Gets DOM application div element and style.display property changes -- this is how we know if our app is in the window
  useEffect(() => {
    const appNode = document.getElementsByClassName('react-main-wrapper-rb')[0] as HTMLElement;
    const appObserverConfig = {
      attributes: true,
      attributeFilter: ['style'],
    };
    const handleAppMutation = (mutationList: MutationRecord[]): void => {
      mutationList.forEach((mutation: MutationRecord) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const displayValue = appNode.style.display;
          if (displayValue === 'block') {
            setIsAppShown(true);
          } else {
            setIsAppShown(false);
          }
        }
      });
    };
    const appObserver = new MutationObserver(handleAppMutation);
    appObserver.observe(appNode, appObserverConfig);

    if (appNode.style.display === 'block') setIsAppShown(true);

    return () => {
      appObserver.disconnect();
    };
  }, []);

  // when browser is refreshed when inside rb app, CCUI does not update the div element style.display property
  useEffect(() => {
    if (window.location.hash.includes(`#/${appCode}`)) {
      setIsAppShown(true);
    } else {
      setIsAppShown(false);
    }
  }, [appCode]);

  // Gets DOM body element and watches for classname changes -- this is how theme is set on main LP framework
  useEffect(() => {
    const targetNode = document.getElementsByTagName('body')[0];
    const observerConfig = { attributes: true, childList: false, subtree: false };
    const handleMutation = (mutationList: MutationRecord[]): void => {
      mutationList.forEach((mutation: MutationRecord) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement;
          const themeColor: ITheme = target.className.includes('theme-dark') ? 'dark' : 'light';
          setTheme(themeColor);
        }
      });
    };
    const observer = new MutationObserver(handleMutation);
    observer.observe(targetNode, observerConfig);

    // This will set initial theme color
    const themeColor: ITheme = targetNode.className.includes('theme-dark') ? 'dark' : 'light';
    setTheme(themeColor);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    console.log('Report Center App version ', packageInfo.version);
  }, []);

  return (
    <GlobalStyles isNewNavbar={true} theme={theme}>
      <Suspense>
        <ReportCenter isAppShown={isAppShown} />
      </Suspense>
    </GlobalStyles>
  );
}

export default App;
